/* eslint-disable id-match */

/**
 * Module dependencies.
 */

import { FormData } from 'src/api/app/user/create-user';
import {
  FormGroup,
  HelpText
} from 'src/components/core/forms/fields-styled-components';

import { color, units } from 'src/styles/utils';
import { regexes } from 'src/core/utils/regexes';
import { theme } from 'styled-tools';
import { useForm } from 'react-hook-form';
import { useTranslate } from 'src/core/utils/translator';
import {
  userJobTitle,
  userProfessionalType,
  userType
} from 'src/core/user-config';

import Button from 'src/components/core/buttons/button';
import Checkbox from 'src/components/core/checkbox';
import Input from 'src/components/core/forms/fields/input';
import Password from 'src/components/core/forms/fields/password';
import PrivacyPolicyCheckboxLabel from 'src/components/core/forms/privacy-policy-checkbox-label';
import RadioButton from 'src/components/core/radio-button';
import React, { ReactElement, useCallback } from 'react';
import Select from 'src/components/core/forms/fields/select';
import get from 'lodash/get';
import map from 'lodash/map';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import useCountries from 'src/api/app/countries/use-countries';
import useDistricts from 'src/api/app/countries/use-districts';

/**
 * Password length.
 */

const passwordLength = 6;

/**
 * `Props` type.
 */

type Props = {
  isSignUpComplete?: boolean,
  isSubmitting: boolean,
  onSubmit: (formData: FormData) => void
};

/**
 * `RadioButtonsWrapper` styled component.
 */

const RadioButtonsWrapper = styled.div`
  margin-bottom: ${units(6)};
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled.label`
  color: ${color('grey900')};
  font-family: ${theme('typography.fontFamily.serif')};
  font-size: ${units(2.5)};
  font-weight: 400;
  line-height: ${units(4)};
  pointer-events: none;
  white-space: nowrap;
`;

/**
 * `StyledFormGroup` styled component.
 */

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: ${units(2)};
`;

/**
 * `StyledCheckbox` styled component.
 */

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: ${units(6)};
`;

/**
 * Normalize form data.
 */

function normalizeFormData({ formData, isSignUpComplete }): FormData {
  const {
    company,
    country,
    district,
    jobTitle,
    location,
    name,
    phoneNumber,
    professionalType,
    subscribeNewsletter,
    type
  } = formData;

  return {
    ...!isSignUpComplete && pick(formData, ['email', 'password']),
    country_alpha2: get(country, 'value'),
    name,
    phone_number: phoneNumber,
    subscribeNewsletter,
    type,
    ...district ? { district_id: get(district, 'value') } : { location },
    ...type === userType.reseller && {
      company,
      job_title: get(jobTitle, 'value')
    },
    ...type === userType.professional && {
      company,
      professional_type: get(professionalType, 'value')
    }
  };
}

/**
 * `SignUpForm` page.
 */

const SignUpForm = (props: Props): ReactElement => {
  const { isSignUpComplete, isSubmitting, onSubmit } = props;
  const { translate } = useTranslate();
  const {
    data: countriesData,
    isLoading: isLoadingCountries
  } = useCountries();

  const {
    data: districtsData,
    isLoading: isLoadingDistricts
  } = useDistricts();

  const professionalTypeOptions = sortBy(Object
    .entries(userProfessionalType)
    .map(([key, value]) => ({
      label: translate(`forms:professionalType.${key}`),
      value
    })), option => {
    if (option.value !== userProfessionalType.other) {
      return option.label;
    }
  });

  const jobTitleOptions = sortBy(Object
    .entries(userJobTitle)
    .map(([key, value]) => ({
      label: translate(`forms:jobTitle.${key}`),
      value
    })), option => {
    if (option.value !== userJobTitle.other) {
      return option.label;
    }
  });

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    watch
  } = useForm({ mode: 'onBlur' });

  const watchUserType = watch('type');
  const watchCountry = watch('country', 'PT');
  const isLoading = isSubmitting ?? formState?.isSubmitting;
  const onSubmitForm = useCallback(formData => {
    const normalizedData = normalizeFormData({ formData, isSignUpComplete });

    onSubmit(normalizedData);
  }, [isSignUpComplete, onSubmit]);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <RadioButtonsWrapper>
        <StyledLabel>
          {translate('forms:userTypeLabel')}
        </StyledLabel>

        {Object.entries(userType).map(([key, value], index: number) => (
          <RadioButton
            defaultChecked={index === 0}
            disabled={isSubmitting}
            key={index}
            label={translate(`forms:userType.${key}`)}
            name={'type'}
            ref={register}
            value={value}
          />
        ))}
      </RadioButtonsWrapper>

      <Input
        disabled={isLoading}
        error={errors?.name}
        label={translate('forms:nameLabel')}
        name={'name'}
        placeholder={translate('forms:namePlaceholder')}
        ref={register<any>({
          required: {
            message: translate('forms:requiredField'),
            value: true
          }
        })}
      />

      <Input
        disabled={isLoading}
        error={errors?.email}
        label={translate('forms:emailLabel')}
        name={'email'}
        placeholder={translate('forms:emailPlaceholder')}
        ref={register<any>({
          pattern: {
            message: translate('forms:emailPatternValidation'),
            value: regexes.email
          },
          required: {
            message: translate('forms:requiredField'),
            value: true
          }
        })}
        type={'email'}
      />

      {(watchUserType === userType.professional || watchUserType === userType.reseller) && (
        <Input
          disabled={isLoading}
          error={errors?.companyName}
          label={translate('forms:companyNameLabel')}
          name={'company'}
          placeholder={translate('forms:companyNamePlaceholder')}
          ref={register}
        />
      )}

      {watchUserType === userType.professional && (
        <Select
          control={control}
          disabled={isLoading}
          error={errors?.professionalType}
          label={translate('forms:professionLabel')}
          name={'professionalType'}
          options={professionalTypeOptions}
          placeholder={translate('forms:selectPlaceholder')}
        />
      )}

      {watchUserType === userType.reseller && (
        <Select
          control={control}
          disabled={isLoading}
          error={errors?.jobTitle}
          label={translate('forms:jobTitleLabel')}
          name={'jobTitle'}
          options={jobTitleOptions}
          placeholder={translate('forms:selectPlaceholder')}
        />
      )}

      <Input
        disabled={isLoading}
        error={errors?.phoneNumber}
        label={translate('forms:phoneNumberLabel')}
        name={'phoneNumber'}
        placeholder={translate('forms:phoneNumberPlaceholder')}
        ref={register}
      />

      <Select
        control={control}
        disabled={isSubmitting}
        error={errors?.country}
        isLoading={isLoadingCountries}
        label={translate('forms:countryLabel')}
        name={'country'}
        options={map(countriesData, ({ alpha2, name }) => ({
          label: name,
          value: alpha2
        }))}
        placeholder={translate('forms:selectPlaceholder')}
        rules={{ required: {
          message: translate('forms:requiredField'),
          value: true
        } }}
      />

      {watchCountry.value === 'PT' ? (
        <Select
          control={control}
          disabled={isSubmitting}
          error={errors?.district}
          isLoading={isLoadingDistricts}
          label={translate('forms:districtLabel')}
          name={'district'}
          options={map(districtsData, ({ id, name }) => ({
            label: name,
            value: id
          }))}
          placeholder={translate('forms:districtPlaceholder')}
          rules={{ required: {
            message: translate('forms:requiredField'),
            value: true
          } }}
        />
      ) : (
        <Input
          disabled={isSubmitting}
          error={errors?.location}
          label={translate('forms:districtLabel')}
          name={'location'}
          placeholder={translate('forms:districtPlaceholder')}
          ref={register<any>({
            required: {
              message: translate('forms:requiredField'),
              value: true
            }
          })}
        />
      )}

      <Password
        disabled={isLoading}
        error={errors?.password}
        helpText={translate('forms:newPasswordHelpText', { passwordLength })}
        label={translate('forms:passwordLabel')}
        name={'password'}
        placeholder={translate('forms:passwordPlaceholder')}
        ref={register<any>({
          pattern: {
            message: translate('forms:passwordSizeValidation', { passwordLength }),
            value: regexes.passwordRequirements.size
          },
          required: {
            message: translate('forms:requiredField'),
            value: true
          }
        })}
      />

      <StyledFormGroup>
        <Checkbox
          alignment={'flex-start'}
          disabled={isLoading}
          i18nKey={'forms:privacyPolicyContactsCheckbox'}
          label={
            <PrivacyPolicyCheckboxLabel
              i18nKey={'forms:privacyPolicySignUpCheckbox'}
            />
          }
          name={'acceptPrivacyPolicy'}
          ref={register<any>({
            required: {
              message: translate('forms:privacyPolicyError'),
              value: true
            }
          })}
        />

        {errors?.acceptPrivacyPolicy && (
          <HelpText hasError>
            {errors?.acceptPrivacyPolicy.message}
          </HelpText>
        )}
      </StyledFormGroup>

      <StyledCheckbox
        alignment={'flex-start'}
        disabled={isSubmitting}
        label={translate('forms:newsletterCheckbox')}
        name={'subscribeNewsletter'}
        ref={register}
      />

      <Button
        colorTheme={'primary'}
        isLoading={isLoading}
        type={'submit'}
        variant={'fill'}
      >
        {translate('authentication:signUp.submitButton')}
      </Button>
    </form>
  );
};

/**
 * Export `SignUpForm` page.
 */

export default SignUpForm;
